import { useState, type ReactElement, type ReactNode } from 'react';
import { authProblem, initials } from './authModel';
import type { AuthApi } from './AuthApi';
import type { AuthUser } from './types';
import { t } from '../shared/i18n';

interface PlatformFrameProps {
  api: AuthApi;
  user: AuthUser;
  path: string;
  children: ReactNode;
  onSignedOut: (notice?: string) => void;
}

const navigation = [
  ['/admin/stations', t('nav.stations')],
  ['/admin', t('nav.schedule')],
  ['/admin/library', t('nav.library')],
  ['/studio', t('nav.studio')],
  ['/dj', t('nav.mobile')],
  ['/admin/monitoring', t('nav.monitoring')],
] as const;

export function PlatformFrame({ api, user, path, children, onSignedOut }: PlatformFrameProps): ReactElement {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function signOut(): Promise<void> {
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      await api.logout();
      onSignedOut('Ви безпечно вийшли з платформи.');
    } catch (reason: unknown) {
      setError(authProblem(reason));
      setPending(false);
    }
  }

  return <div className="platform-frame">
    <header className="platform-bar">
      <a className="platform-brand" href="/admin" aria-label="SweetLana, панель керування"><span>SL</span><strong>SWEETLANA</strong></a>
      <nav aria-label="Розділи платформи">
        {navigation.map(([href, label]) => <a key={href} href={href} className={isActive(path, href) ? 'active' : ''} aria-current={isActive(path, href) ? 'page' : undefined}>{label}</a>)}
      </nav>
      <div className="platform-session">
        <span className="platform-secure"><i /> СЕСІЯ АКТИВНА</span>
        <details>
          <summary aria-label={`Меню користувача ${user.displayName}`}><span>{initials(user.displayName, user.email)}</span><b>{user.displayName}</b></summary>
          <div className="account-popover">
            <div><strong>{user.displayName}</strong><small>{user.email}</small></div>
            <a href="/account">Безпека акаунта <span>→</span></a>
            <a href="/" target="_blank" rel="noreferrer">Відкрити сайт слухача <span>↗</span></a>
            <button type="button" onClick={() => void signOut()} disabled={pending}>{pending ? 'ВИХІД…' : 'ВИЙТИ'}</button>
            {error !== null && <p role="alert">{error}</p>}
          </div>
        </details>
      </div>
    </header>
    <div className="platform-frame-content">{children}</div>
  </div>;
}

function isActive(path: string, href: string): boolean {
  if (href === '/admin') return path === '/admin' || path.startsWith('/admin/scheduler');
  return path === href || path.startsWith(`${href}/`);
}
